import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  ClipboardCheck,
  Flame,
  Skull,
  Trophy,
  ArrowLeft,
  Star,
  CheckCircle2,
  XCircle,
  Sparkles,
  Award,
} from "lucide-react";

/**
 * Manager Check-In — daily accountability form + public scoreboard.
 * Every manager submits 4 fields the GM designed. Streaks are public.
 * Missed a check-in? A friendly nudge goes out. Positive tone, not shame.
 */

const BRAND_BRONZE = "#a06a1e";
const BRAND_BRONZE_DARK = "#7a4e0f";
const BRAND_CREAM = "#f6ecd8";

const MANAGERS = [
  { role: "Recon Manager",     name: "Marcus Reyes" },
  { role: "Body Shop Manager", name: "Danny Chen" },
  { role: "Used Car Manager",  name: "Steve Thompson" },
  { role: "Photos / Merch",    name: "Tanya Rodriguez" },
  { role: "General Manager",   name: "Sarah Patel" },
];

export default function ManagerCheckinPage() {
  const [today, setToday] = useState(null);
  const [board, setBoard] = useState(null);
  const [selectedRole, setSelectedRole] = useState("Recon Manager");
  const [form, setForm] = useState({
    board_current: true,
    top_blocker: "",
    moved_today: 0,
    expected_tomorrow: 0,
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [t, s] = await Promise.all([
        api.get("/posse/checkin/today").then((r) => r.data),
        api.get("/posse/scoreboard").then((r) => r.data),
      ]);
      setToday(t);
      setBoard(s);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    setSaving(true);
    try {
      const dep = MANAGERS.find((d) => d.role === selectedRole);
      await api.post("/posse/checkin", {
        role: selectedRole,
        name: dep?.name,
        board_current: form.board_current,
        top_blocker: form.top_blocker,
        moved_today: Number(form.moved_today) || 0,
        expected_tomorrow: Number(form.expected_tomorrow) || 0,
      });
      toast.success(`${dep?.name} checked in for tonight`);
      setForm({ board_current: true, top_blocker: "", moved_today: 0, expected_tomorrow: 0 });
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const submitted = today?.submitted_count || 0;
  const total = today?.total || 5;
  const wanted = board?.most_wanted || [];
  const scores = board?.scoreboard || [];
  const onWatch = scores.filter((s) => s.on_redemption_watch);
  const redeemed = scores.filter((s) => s.has_redemption_badge);

  return (
    <DashboardShell>
      {/* Hero */}
      <div
        className="mb-4 rounded-xl overflow-hidden shadow-xl relative"
        style={{
          background: `linear-gradient(135deg, ${BRAND_BRONZE_DARK} 0%, ${BRAND_BRONZE} 60%, #c98c3d 100%)`,
        }}
        data-testid="checkin-hero"
      >
        <div className="relative p-5 md:p-6 flex items-center gap-4 flex-wrap">
          <div
            className="w-14 h-14 rounded-full grid place-items-center shadow-xl shrink-0"
            style={{ background: BRAND_CREAM }}
          >
            <ClipboardCheck className="w-7 h-7" strokeWidth={2.5} style={{ color: BRAND_BRONZE_DARK }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono-label text-[0.6rem] text-white/85 tracking-widest">
              PREOWNED PIPELINE · NIGHTLY CHECK-IN
            </div>
            <div className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white leading-none mt-1">
              Manager Check-In
            </div>
            <div className="font-display text-white/90 text-sm md:text-base mt-1">
              {submitted} of {total} managers checked in today · 8am · Noon · 6pm
            </div>
          </div>
          <Link to="/posse">
            <Button
              variant="outline"
              className="bg-white/95 hover:bg-white text-neutral-900 border-transparent"
              data-testid="checkin-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
              Back to Pipeline
            </Button>
          </Link>
        </div>
      </div>

      {/* Friendly Nudge — positive callout for managers who missed 8am / noon /
          6pm. No shame wall — just a warm "hop on!" message that helps them
          stay on top of the board. */}
      {wanted.length > 0 && (
        <Card
          className="p-5 mb-5 border-2 border-amber-500 bg-gradient-to-r from-amber-50 to-yellow-50"
          data-testid="checkin-most-wanted"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-amber-600" strokeWidth={2.4} />
            <div className="font-display font-black text-lg tracking-tight text-amber-900">
              Friendly Nudge
            </div>
            <div className="ml-auto font-mono-label text-[0.6rem] text-amber-700 tracking-widest">
              MISSED A CHECK-IN · WE'VE GOT YOUR BACK
            </div>
          </div>
          <p className="text-xs text-amber-900/80 mb-4 leading-relaxed">
            These managers missed one of today's check-ins (8am · Noon · 6pm). We'll auto-text
            a quick reminder — no drama, no wall of shame. Just a nudge to hop on the board.
          </p>
          <div className="grid md:grid-cols-3 gap-3">
            {wanted.map((w) => (
              <div
                key={w.role}
                className="rounded-md border-2 border-amber-400 bg-white/80 p-3"
                data-testid={`checkin-wanted-${w.role.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="font-mono-label text-[0.55rem] text-amber-800 tracking-widest">
                  QUICK REMINDER
                </div>
                <div className="font-display font-black text-xl text-neutral-900 mt-1 leading-tight">
                  Hey {w.name?.split(" ")[0]} — hop on!
                </div>
                <div className="text-[0.7rem] text-neutral-700 mt-1">{w.role}</div>
                <div className="mt-2 pt-2 border-t border-amber-300 text-[0.7rem] text-amber-900">
                  Last check-in {w.last_submitted ? new Date(w.last_submitted).toLocaleDateString() : "not yet today"}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Form + Scoreboard side-by-side */}
      <div className="grid md:grid-cols-[1fr_1.1fr] gap-4">
        {/* Nightly form */}
        <Card
          className="p-5 border-2"
          style={{ borderColor: BRAND_BRONZE_DARK, background: `${BRAND_CREAM}55` }}
          data-testid="checkin-form"
        >
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-4 h-4" style={{ color: BRAND_BRONZE_DARK }} strokeWidth={2.4} />
            <div className="font-mono-label text-[0.65rem] tracking-widest" style={{ color: BRAND_BRONZE_DARK }}>
              TONIGHT'S CHECK-IN · GM'S 4-QUESTION FORM
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="font-mono-label text-[0.55rem] tracking-widest text-neutral-700 mb-1 block">
                MANAGER
              </label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                data-testid="checkin-role"
              >
                {MANAGERS.map((d) => (
                  <option key={d.role} value={d.role}>
                    {d.name} — {d.role}
                  </option>
                ))}
              </select>
            </div>

            <div className="rounded-md border border-border bg-background p-3 flex items-center justify-between">
              <div>
                <div className="font-mono-label text-[0.55rem] tracking-widest text-neutral-700">
                  1 · BOARD IS CURRENT
                </div>
                <div className="text-xs text-muted-foreground">
                  Every one of your cars is up-to-date right now
                </div>
              </div>
              <Switch
                checked={form.board_current}
                onCheckedChange={(v) => setForm({ ...form, board_current: v })}
                data-testid="checkin-board-current"
              />
            </div>

            <div>
              <label className="font-mono-label text-[0.55rem] tracking-widest text-neutral-700 mb-1 block">
                2 · TOP BLOCKER ON YOUR OLDEST CAR
              </label>
              <Textarea
                placeholder="e.g. Waiting on trans mount for the '23 Silverado — ETA Thursday"
                value={form.top_blocker}
                onChange={(e) => setForm({ ...form, top_blocker: e.target.value })}
                rows={2}
                data-testid="checkin-blocker"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-mono-label text-[0.55rem] tracking-widest text-neutral-700 mb-1 block">
                  3 · CARS MOVED TODAY
                </label>
                <Input
                  type="number"
                  min={0}
                  value={form.moved_today}
                  onChange={(e) => setForm({ ...form, moved_today: e.target.value })}
                  data-testid="checkin-moved"
                />
              </div>
              <div>
                <label className="font-mono-label text-[0.55rem] tracking-widest text-neutral-700 mb-1 block">
                  4 · FRONT-LINE HITS TOMORROW
                </label>
                <Input
                  type="number"
                  min={0}
                  value={form.expected_tomorrow}
                  onChange={(e) => setForm({ ...form, expected_tomorrow: e.target.value })}
                  data-testid="checkin-tomorrow"
                />
              </div>
            </div>

            <Button
              onClick={submit}
              disabled={saving}
              className="w-full h-11 font-bold"
              style={{ background: BRAND_BRONZE_DARK, color: "white" }}
              data-testid="checkin-submit"
            >
              <ClipboardCheck className="w-4 h-4 mr-2" strokeWidth={2.4} />
              {saving ? "Locking it in…" : "Check In · Keep the Streak Alive"}
            </Button>
          </div>
        </Card>

        {/* Scoreboard */}
        <Card
          className="p-5 border-2"
          style={{ borderColor: BRAND_BRONZE, background: "#0a0a0a" }}
          data-testid="checkin-scoreboard"
        >
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-yellow-300" strokeWidth={2.4} />
            <div className="font-mono-label text-[0.65rem] tracking-widest text-yellow-300">
              MANAGER SCOREBOARD · PUBLIC · 30-DAY LOOKBACK
            </div>
          </div>
          <p className="text-[0.7rem] text-white/60 mb-3 italic">
            Everyone sees these numbers — the managers, the GM, and yes, the visiting fixed ops
            consultant. Streaks build trust. Check in at 8am, Noon, and 6pm.
          </p>

          <div className="space-y-2">
            {scores.map((s, idx) => {
              const isTop = idx === 0 && s.current_streak > 0;
              const isMissed = s.consecutive_missed > 0;
              const isRedeemed = s.has_redemption_badge;
              return (
                <div
                  key={s.role}
                  className={`rounded-lg p-3 border ${
                    isMissed
                      ? "border-red-700 bg-red-950/40"
                      : isRedeemed
                      ? "border-yellow-500 bg-gradient-to-r from-yellow-500/10 to-amber-500/10"
                      : "border-yellow-500/30 bg-yellow-500/5"
                  }`}
                  data-testid={`checkin-score-${s.role.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="flex items-baseline gap-3">
                    <div className="font-display font-black text-lg text-white truncate">
                      {isTop && <span className="mr-1">🏆</span>}
                      {isRedeemed && <span className="mr-1">🎖️</span>}
                      {s.name}
                    </div>
                    {isRedeemed && (
                      <div className="rounded px-1.5 py-0.5 bg-yellow-500 text-yellow-950 text-[0.55rem] font-black tracking-widest">
                        BACK ON TRACK
                      </div>
                    )}
                    <div className="ml-auto flex items-baseline gap-1 shrink-0">
                      <Flame
                        className={`w-4 h-4 ${s.current_streak > 0 ? "text-orange-400" : "text-neutral-500"}`}
                        strokeWidth={2.4}
                      />
                      <div className="font-display font-black text-2xl tabular-nums text-yellow-300">
                        {s.current_streak}
                      </div>
                      <div className="text-[0.55rem] text-white/60 tracking-widest">DAY STREAK</div>
                    </div>
                  </div>
                  <div className="text-[0.6rem] text-white/60 flex flex-wrap gap-x-3 mt-1">
                    <span>Best: {s.longest_streak}d</span>
                    <span>Total checked: {s.total_days_checked_in} / 30</span>
                    {isMissed && (
                      <span className="text-red-300 font-bold">
                        Missed last {s.consecutive_missed}d
                      </span>
                    )}
                    <span className="ml-auto">
                      Last: {s.last_submitted ? new Date(s.last_submitted).toLocaleDateString() : "never"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Tonight's roll-call */}
      {today && (
        <Card className="p-4 mt-4 border-border" data-testid="checkin-today-rollcall">
          <div className="font-mono-label text-[0.6rem] tracking-widest text-muted-foreground mb-2">
            TODAY'S ROLL CALL · {today.date} · 8am · Noon · 6pm
          </div>
          <div className="flex flex-wrap gap-2">
            {today.deputies?.map((d) => (
              <div
                key={d.role}
                className={`rounded-full px-3 py-1 border text-[0.65rem] flex items-center gap-1.5 ${
                  d.submitted
                    ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                    : "border-amber-400 bg-amber-50 text-amber-900"
                }`}
                data-testid={`checkin-rollcall-${d.role.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {d.submitted ? (
                  <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.4} />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" strokeWidth={2.4} />
                )}
                <span className="font-mono-label text-[0.55rem] tracking-widest">{d.role.toUpperCase()}</span>
                <span className="font-semibold">{d.name}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </DashboardShell>
  );
}
